import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import ReactEcharts, { EChartsOption } from "echarts-for-react";
import { deepMerge } from "../../../../app/deepMerge";
import { useAppSelector } from "../../../../app/hooks";
import { miwChartColors, tooltipMark, utahChartColors } from "../../shared/reportUtil";
import { NoChartData } from "../../shared/NoChartData";

export interface TraitChartFilter {
	value: string;
	label: string;
	maxValue: number;
}

interface TraitScoreEChartsData {
	name: string;
	value: number | null;
	link: string;
}

function initializeOptions(
	chartData: TraitScoreEChartsData[],
	dataLabel: string,
	average?: number,
	averageLabel?: string,
	maxValue?: number,
	xAxisInterval?: number
): EChartsOption {
	const { config } = useAppSelector((state) => state.site);
	let chartColors = config?.isUtahComposeSite ? utahChartColors : miwChartColors;

	const { foregroundColor, chartPalette, accentColor } = useAppSelector(
		(state) => state.contrast
	);

	if (chartPalette) {
		chartColors = chartPalette.slice(0, 2);
	}

	const option: EChartsOption = {
		textStyle: {
			color: foregroundColor,
			fontFamily: "Montserrat",
			fontWeight: "500",
		},
		grid: {
			containLabel: true,
			top: 40,
			left: 10,
			right: 30,
			bottom: 25,
		},
		xAxis: {
			type: "value",
			min: 0,
			max: maxValue,
			axisLine: {
				show: true,
				lineStyle: {
					color: foregroundColor,
				},
			},
			name: dataLabel + " Average Score",
			nameLocation: "middle",
			nameGap: 30,
			interval: xAxisInterval,
			splitLine: {
				show: true,
				lineStyle: {
					color: accentColor,
				},
			},
		},
		yAxis: {
			type: "category",
			data: chartData.map((d) => d.name),
			inverse: true,
			axisLabel: {
				padding: 25,
				lineHeight: 15,
				interval: 0,
				width: 180,
				overflow: "break",
				hideOverlap: false,
			},
			axisLine: {
				show: true,
				lineStyle: {
					color: foregroundColor,
				},
			},
		},
		tooltip: {
			trigger: "item",
			formatter: (params: { value: string; name: string; color: string }) => {
				const heading = `<div>${params.name.split("\n")[0]}</div>`;
				const lineItem = `<div>${tooltipMark(params.color)} ${dataLabel}: <b>${params.value}</b></div>`;
				const avgLine = `<div>${tooltipMark("red")} Average: <b>${average}</b></div>`;
				return heading + lineItem + avgLine;
			},
			textStyle: {
				fontSize: 13,
			},
		},
		legend: {
			data: [
				{
					name: dataLabel,
					itemStyle: {
						color: chartColors[0],
					},
				},
				{
					name: averageLabel ? `${averageLabel} Average` : "Average",
					icon: "circle",
				},
			],
			show: true,
			padding: 10,
			itemGap: 20,
			textStyle: {
				color: foregroundColor,
			},
			top: 0,
		},
		animationDurationUpdate: 500,
		series: [
			{
				type: "bar",
				id: "category",
				name: dataLabel,
				data: chartData.map((d) => ({ value: d.value, link: d.link })),
				itemStyle: {
					color: function (param: { dataIndex: number }) {
						return chartColors[param.dataIndex % chartColors.length];
					},
				},
				label: {
					show: true,
					position: ["100%", "50%"],
					height: 25,
					width: 25,
					align: "center",
					verticalAlign: "middle",
					padding: 4,
					backgroundColor: "#fff",
					borderRadius: 25,
					borderColor: "inherit",
					borderWidth: 1,
					fontWeight: "bolder",
					fontSize: 12,
				},
				barMinWidth: 40,
				emphasis: {
					itemStyle: {
						shadowBlur: 10,
					},
				},
				universalTransition: {
					enabled: true,
					divideShape: "clone",
				},
			},
			{
				name: averageLabel ? `${averageLabel} Average` : "Average",
				type: "line",
				color: "red",
				symbol: "none",
				markLine: {
					name: "Average Line",
					data: [
						{
							xAxis: average,
						},
					],
					label: {
						show: false,
					},
					lineStyle: {
						color: "red",
					},
					symbol: ["circle", "circle"],
					silent: true,
				},
			},
		],
	};
	return option;
}

export function TraitScoreEChart(props: {
	data: TraitScoreEChartsData[];
	dataLabel: string;
	average?: number;
	averageLabel?: string;
	maxValue?: number;
	options?: EChartsOption;
}) {
	const chartRef = useRef<ReactEcharts>(null);
	const navigate = useNavigate();

	const chartHeight = props.data.length * 60 + 95;
	const xAxisInterval = props.maxValue ? (props.maxValue > 5 ? 5 : 1) : undefined;
	const initialOptions = initializeOptions(
		props.data,
		props.dataLabel,
		props.average,
		props.averageLabel,
		props.maxValue,
		xAxisInterval
	);

	const options = props.options ? deepMerge(initialOptions, props.options) : initialOptions;

	const onChartClick = (params: any) => {
		if (params.data.link) navigate(params.data.link);
	};

	useEffect(() => {
		// the event handler ReactEcharts provides interferes with
		// transitions when data in the chart changes.
		if (chartRef?.current) {
			const instance = chartRef.current.getEchartsInstance();
			instance.on("click", onChartClick);
		}
	}, [chartRef]);

	if (!props.data || props.data.length === 0)
		return (
			<NoChartData
				height={chartHeight}
				label={`No ${props.dataLabel.toLowerCase()} scores to display.`}
			/>
		);

	return (
		<ReactEcharts
			ref={chartRef}
			option={options}
			className="w-full"
			style={{
				height: chartHeight,
			}}
		/>
	);
}
